// Live reload from `bs serve`: the files it serves show up as examples, and saving one on disk
// reloads it here.

export interface LocalFile {
  path: string;
  title: string;
}

export interface ServeHandlers {
  /** The current list of files, after connecting and whenever one is added or removed. */
  files(list: LocalFile[]): void;
  /** A file was added, changed or removed on disk. */
  changed(path: string): void;
  /** Whether the server is reachable. */
  status(online: boolean): void;
}

const PORT = 7357;

/**
 * Where bs serve is for this page: `?serve=host:port` (or just `?serve` for localhost), or the
 * page's own origin when bs serve is serving the testbed. null means don't connect.
 */
export function serveBase(params: URLSearchParams): string | null {
  if (params.has('serve')) {
    const value = params.get('serve')?.trim() || 'localhost';
    const address = /:\d+$/.test(value) ? value : `${value}:${PORT}`;
    return /^https?:\/\//.test(address) ? address : `http://${address}`;
  }

  if (document.querySelector('meta[name="bs-serve"]')) return location.origin;
  return null;
}

export class Serve {
  private events: EventSource | null = null;

  constructor(
    readonly base: string,
    private readonly handlers: ServeHandlers,
  ) {}

  /** host:port, for the stats. */
  get label(): string {
    return new URL(this.base).host;
  }

  connect(): void {
    this.events?.close();
    const events = new EventSource(`${this.base}/__bs/events`);
    this.events = events;

    // Every connection, including after a restart of bs serve, lists the files again.
    events.addEventListener('open', () => {
      this.handlers.status(true);
      void this.list().catch(() => this.handlers.status(false));
    });

    events.addEventListener('change', (e) => {
      const { path } = JSON.parse((e as MessageEvent<string>).data) as { path: string };
      this.handlers.changed(path);
    });

    // The browser retries on its own after a network error, but gives up after a bad response,
    // so try again from scratch then.
    events.addEventListener('error', () => {
      this.handlers.status(false);
      if (events.readyState === EventSource.CLOSED) setTimeout(() => this.connect(), 2000);
    });
  }

  async list(): Promise<LocalFile[]> {
    const res = await fetch(`${this.base}/__bs/files`, { cache: 'no-store' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const list = (await res.json()) as LocalFile[];
    this.handlers.files(list);
    return list;
  }

  fileUrl(path: string): string {
    return `${this.base}/__bs/file?path=${encodeURIComponent(path)}`;
  }

  async source(path: string): Promise<string> {
    const res = await fetch(this.fileUrl(path), { cache: 'no-store' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.text();
  }
}
